import React, { useEffect, useState } from "react";

import "./Analytics.css";
import {
  getSurveyStatistic,
  getRegisterDoctorInLastMonth,
  getAdheranceStatistic,
  getPatientEngagementBySymtoms,
} from "../services/analyticsService";
import { useSelector } from "react-redux";

import { Line, Doughnut, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register required chart elements
ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);
// Register required chart components
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

function StatisticCharts() {
  const token = useSelector((state) => state?.login?.data?.accessToken);

  const [statistic, setStatistic] = useState({
    totalAssignSurvey: 0,
    totalCompleteSurvey: 0,
    registerDoctorPerMonth: {
      labels: [],
      totalDoctorsPerMonth: [],
    },
    activeDoctorsPerMonth: {
      // "labels" : [],
      totalActiveDoctorsPerMonth: [],
    },
    activePatientPerDay: {
      labels: [],
      totalActivePatientPerDay: [],
    },
    activeDoctorPerDay: {
      labels: [],
      totalActiveDoctorPerDay: [],
    },
    treatmentAdherance: {
      labels: [],
      treatmentAdheranceData: [],
    },
  });

  // Common function to fetch all required data
  const _fetchStatisticData = async () => {
    try {
      const [
        surevyResponse,
        RegisterDoctorInLastMonth,
        AdheranceData,
        patientEngagement,
      ] = await Promise.all([
        getSurveyStatistic(token),
        getRegisterDoctorInLastMonth(token), //LAST 6 MONTHS
        getAdheranceStatistic(token),
        getPatientEngagementBySymtoms(token),
      ]);

      setStatistic({
        totalAssignSurvey: surevyResponse?.data?.allAssignCount || 0,
        totalCompleteSurvey: surevyResponse?.data?.allCompletedCount || 0,
        registerDoctorPerMonth: {
          labels: RegisterDoctorInLastMonth?.data?.labels || [],
          totalDoctorsPerMonth:
            RegisterDoctorInLastMonth?.data?.totalDoctorsPerMonth || [],
        },

        activeDoctorsPerMonth: {
          // labels: RegisterDoctorInLastMonth?.data?.labels || [],
          totalActiveDoctorsPerMonth:
            RegisterDoctorInLastMonth?.data?.activeDoctorsPerMonth || [],
        },
        activePatientPerDay: {
          labels: patientEngagement?.data?.symptomDates || [],
          totalActivePatientPerDay:
            patientEngagement?.data?.symptomCountPerDay || [],
        },
        activeDoctorPerDay: {
          labels:
            RegisterDoctorInLastMonth?.data?.DoctorFormattedData.doctorDates ||
            [],
          totalActiveDoctorPerDay:
            RegisterDoctorInLastMonth?.data?.DoctorFormattedData
              .doctorCountPerDay || [],
        },
        treatmentAdherance: {
          labels: AdheranceData?.data?.labels || [],
          treatmentAdheranceData: AdheranceData?.data?.data || [],
        },
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    _fetchStatisticData();
  }, []);

  const surveyData = {
    assignedSurveys: statistic?.totalAssignSurvey || 0,
    completedSurveys: statistic?.totalCompleteSurvey || 0,
  };

  // ***********************************  Pie Chart ******************************************

  // Prepare data for Pie Chart for surveys
  const data = {
    labels: ["Completed Surveys", "Pending Surveys"],
    datasets: [
      {
        data: [surveyData.completedSurveys, surveyData.assignedSurveys],
        backgroundColor: ["#36A2EB", "#FF6384"], // Colors for sections
        hoverBackgroundColor: ["#4BC0C0", "#FF9F40"],
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      datalabels: {
        color: "white",
        font: { weight: "bold", size: 14 },
        anchor: "center", // Position text in the middle of each section
        align: "center",
      },
    },
  };

  // ************************** Two-Line Chart Data **************************

  const providerData = {
    labels: statistic?.registerDoctorPerMonth?.labels, // X-axis months
    datasets: [
      {
        label: "Total Registered Doctors",
        data: statistic?.registerDoctorPerMonth?.totalDoctorsPerMonth || [], // Total registrations
        borderColor: "#36A2EB",
        backgroundColor: "rgba(54, 162, 235, 0.5)",
        tension: 0.4, // Smooth curve
        fill: false,
      },
      {
        label: "Active Doctors",
        data:
          statistic?.activeDoctorsPerMonth?.totalActiveDoctorsPerMonth || [], // Active doctors data
        borderColor: "#FF6384",
        backgroundColor: "rgba(255, 99, 132, 0.5)",
        tension: 0.4,
        fill: false,
      },
    ],
  };

  // Chart options for a single Y-axis
  const provideOptions = {
    layout: {
      padding: {
        top: 30, // Increase space at the top
      },
    },
    responsive: true,
    scales: {
      y: {
        type: "linear",
        position: "left",
        title: { display: true, text: "Number of Doctors" },
      },
    },
    plugins: {
      legend: { position: "bottom" },
      datalabels: {
        color: "black",
        font: { weight: "bold", size: 12 },
        anchor: "end", // Position text above points
        align: "top",
      },
    },
  };

  // *********************************** Doughnut chart ******************************************

  const numericalData =
    statistic?.treatmentAdherance.treatmentAdheranceData.map((value) =>
      parseFloat(value)
    );

  const adheranceData = {
    labels: statistic.treatmentAdherance.labels,
    datasets: [
      {
        data: numericalData,
        backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"],
        hoverBackgroundColor: ["#4BC0C0", "#FF9F40", "#FFCD56"],
      },
    ],
  };

  const adheranceDataOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      datalabels: {
        color: "#fff", // Label text color
        font: { weight: "bold", size: 14 },
        anchor: "center", // Position text in the middle of each section
        align: "center",
        formatter: (value) => `${value}%`, // Append "%" symbol
      },
    },
  };

  // ************************** Line Chart Data (Last 30 Days) **************************

  const engagementData = {
    labels: statistic.activePatientPerDay?.labels || [],
    datasets: [
      {
        label: "Daily Patient Engagement",
        data: statistic.activePatientPerDay.totalActivePatientPerDay || [],
        borderColor: "#36A2EB",
        backgroundColor: "rgba(54, 162, 235, 0.5)",
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointBackgroundColor: "#36A2EB",
      },

      // {
      //   label: "Daily Doctor Engagement",
      //   data: statistic.activeDoctorPerDay.totalActiveDoctorPerDay || [],
      //   borderColor: "#FF6384",
      //   backgroundColor: "rgba(255, 99, 132, 0.5)",
      //   tension: 0.4,
      //   fill: false,
      //   pointRadius: 4,
      //   pointBackgroundColor: "#FF6384",
      // },
    ],
  };
  // options
  const engagementOptions = {
    layout: {
      padding: {
        top: 30, // Increase space at the top
      },
    },
    responsive: true,
    scales: {
      x: {
        title: { display: true, text: "Date" },
        ticks: { autoSkip: true, maxTicksLimit: 10 },
      },
      y: {
        title: { display: true, text: "Number of Engaged Patients / Doctor" },
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
    plugins: {
      legend: { position: "bottom" },
      datalabels: {
        color: "black",
        font: { weight: "bold", size: 12 },
        anchor: "end",
        align: "top",
        clip: false,
      },
    },
  };

  return (
    <div className="adminDashboard-wrapper">
      <div className="admindashboard-inner-wrapper">
        <div className="row align-items-stretch mb-3">
          {/* Left Side - Image */}
          <div className="col-md-8 mb-2 mb-md-1 d-flex align-items-stretch">
            <div className="w-100 h-100 border chart-wrapper">
              <div className="text-center mb-2 border-bottom p-2">
                {/* <span>Doctor Active/Register Status</span> */}
                <span className="fw-bold wrapper-main-heading">
                  Doctor Adoption Rate (Last 6 Months)
                </span>
              </div>
              <Line
                data={providerData}
                options={provideOptions}
                style={{ padding: "15px" }}
              />
            </div>
          </div>

          {/* Right Side - Charts in Column */}
          <div className="col-md-4 mb-2 mb-md-1  d-flex flex-column gap-4">
            <div className="flex-grow-1 border chart-wrapper">
              <div className="text-center mb-2 border-bottom p-2">
                <span className="fw-bold wrapper-main-heading">
                  Survey Completion Status
                </span>
              </div>
              <Pie data={data} options={options} style={{ padding: "15px" }} />
            </div>
          </div>
        </div>

        <div className="row align-items-stretch mb-3">
          {/* Left Side - Image */}
          <div className="col-md-4 mb-2 mb-md-1   d-flex align-items-stretch">
            <div className="w-100 h-100 border chart-wrapper">
              <div className="text-center mb-2 border-bottom p-2">
                <span className="fw-bold wrapper-main-heading">
                  Adherance Percentage
                </span>
              </div>
              <Doughnut
                data={adheranceData}
                options={adheranceDataOptions}
                style={{ padding: "15px" }}
              />
            </div>
          </div>

          {/* Right Side - Charts in Column */}
          {/* Daily Engagement Chart */}
          <div className="col-md-8 mb-2 mb-md-1  d-flex align-items-stretch">
            <div className="w-100 h-100 border chart-wrapper">
              <div className="text-center mb-2 border-bottom p-2">
                <span className="fw-bold wrapper-main-heading">
                  Daily Patient Engagement (Last 30 Days)
                </span>
              </div>
              <Line
                data={engagementData}
                options={engagementOptions}
                style={{ padding: "15px" }}
              />
            </div>
          </div>
        </div>

        {/* DOCTOR SEPERATE ENGAGMENT FOR LAST 30 DAYS  */}
        {/* <div className="row align-items-stretch mb-3">
      
         
          <div className="col-md-8 mb-2 mb-md-1  d-flex align-items-stretch">
            <div className="w-100 border h-100" >
              <div className="text-center mb-2 border-bottom p-2">
                <span className="fw-bold">Daily Doctor Engagement (Last 30 Days)</span>
              </div>
              <Line
                data={doctorEngagementData}
                options={doctorEngagementOptions}
                style={{ padding: "15px"
                }}
             
              />
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
}

export default StatisticCharts;
